import React from 'react';
import { TablePagination } from '@mui/material';
import { GridColDef, GridRowParams } from '@mui/x-data-grid-pro';
import { OperationStatus } from 'components/operation/models/OperationModel';
import { MuiGrid } from 'lib/MuiGrid/MuiGrid';
import { GridStorageName } from 'lib/MuiGrid/StateController';
import { ExportedDataActionCell } from 'components/analytics/export/tables/ExportedDataActionCell';
import { OperationStatusCell } from 'lib/MuiGrid/OperationStatusCell';
import { IActionOperation, ACTION_OPERATIONS_LIMIT } from 'components/analytics/export/models';
import { formatDateTime } from 'lib/timeHelpers';
import { MuiTheme } from 'config/theme';

interface IExportedDataTableProps {
    operations: IActionOperation[];
    loading: boolean;
    count: number;
    page: number;
    onPageChange: (event: unknown, page: number) => void;
    onRowClick: (params: GridRowParams) => void;
}

export const ExportedDataTable: React.FC<IExportedDataTableProps> = ({
    operations,
    loading,
    count,
    page,
    onPageChange,
    onRowClick
}) => {
    const columns = React.useMemo<GridColDef[]>(
        () => [
            {
                field: 'createdAt',
                headerName: 'Date',
                width: 180,
                sortable: false,
                valueFormatter: (value: string) => formatDateTime(value)
            },
            {
                field: 'status',
                headerName: 'State',
                width: 120,
                sortable: false,
                renderCell: OperationStatusCell
            },
            {
                field: 'message',
                headerName: 'Message',
                flex: 1,
                minWidth: 400,
                sortable: false,
                valueGetter: (_value: unknown, row: IActionOperation) => {
                    if (row.status === OperationStatus.ERROR) {
                        return row.error?.message;
                    }
                    return row.result?.message;
                }
            },
            {
                field: 'action',
                headerName: '',
                width: 60,
                sortable: false,
                disableColumnMenu: true,
                align: 'center',
                renderCell: params => <ExportedDataActionCell operation={params.row} />
            }
        ],
        []
    );

    const getRowId = React.useCallback((row: IActionOperation) => row._id, []);

    const handlePageChange = React.useCallback(
        (_: unknown, newPage: number) => {
            onPageChange(_, newPage);
        },
        [onPageChange]
    );

    const Pagination = React.useCallback(
        () => (
            <TablePagination
                count={count}
                onPageChange={handlePageChange}
                page={page}
                rowsPerPage={ACTION_OPERATIONS_LIMIT}
                rowsPerPageOptions={[ACTION_OPERATIONS_LIMIT]}
                component="div"
                sx={{
                    '&:last-child': {
                        paddingRight: MuiTheme.spacing(6)
                    }
                }}
            />
        ),
        [count, handlePageChange, page]
    );

    return (
        <MuiGrid
            rows={operations}
            columns={columns}
            getRowId={getRowId}
            enableRowClick
            onRowClick={onRowClick}
            storageName={GridStorageName.ActionExports}
            hideFooter={false}
            pagination
            paginationMode="server"
            rowCount={count}
            loading={loading}
            disableColumnFilter
            slots={{
                footer: Pagination
            }}
        />
    );
};
